import { useState, useCallback } from "react";
import { GlossaryBlueprint } from "@/types-games";
import { GlossaryItem } from "@/lib/stores/objectsStore";

/**
 * Hook to manage wizard states for unified item pages
 * Consolidates all wizard open/closed states and selected items
 */
export function useWizardManager() {
  // Wizard open states
  const [isAddWizardOpen, setIsAddWizardOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateWizardOpen, setIsCreateWizardOpen] = useState(false);
  const [isBulkCreateWizardOpen, setIsBulkCreateWizardOpen] = useState(false);
  const [isDialogWizardOpen, setIsDialogWizardOpen] = useState(false);

  // Selected items for editing
  const [selectedBlueprint, setSelectedBlueprint] = useState<GlossaryBlueprint | null>(null);
  const [selectedGlossaryItem, setSelectedGlossaryItem] = useState<GlossaryItem | null>(null);
  const [selectedDialog, setSelectedDialog] = useState<any>(null);

  // Open handlers
  const openAddWizard = useCallback(() => {
    setIsAddWizardOpen(true);
  }, []);

  const openEditModal = useCallback((blueprint?: GlossaryBlueprint, glossaryItem?: GlossaryItem, dialog?: any) => {
    if (blueprint) setSelectedBlueprint(blueprint);
    if (glossaryItem) setSelectedGlossaryItem(glossaryItem);
    if (dialog) setSelectedDialog(dialog);
    setIsEditModalOpen(true);
  }, []);

  const openCreateWizard = useCallback(() => {
    setIsCreateWizardOpen(true);
  }, []);

  const openBulkCreateWizard = useCallback(() => {
    setIsBulkCreateWizardOpen(true);
  }, []);

  const openDialogWizard = useCallback((dialog?: any) => {
    if (dialog) setSelectedDialog(dialog);
    setIsDialogWizardOpen(true);
  }, []);

  // Close handlers
  const closeAddWizard = useCallback(() => {
    setIsAddWizardOpen(false);
  }, []);

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedBlueprint(null);
    setSelectedGlossaryItem(null);
    setSelectedDialog(null);
  }, []);

  const closeCreateWizard = useCallback(() => {
    setIsCreateWizardOpen(false);
  }, []);

  const closeBulkCreateWizard = useCallback(() => {
    setIsBulkCreateWizardOpen(false);
  }, []);

  const closeDialogWizard = useCallback(() => {
    setIsDialogWizardOpen(false);
    setSelectedDialog(null);
  }, []);

  // Close all wizards
  const closeAllWizards = useCallback(() => {
    setIsAddWizardOpen(false);
    setIsEditModalOpen(false);
    setIsCreateWizardOpen(false);
    setIsBulkCreateWizardOpen(false);
    setIsDialogWizardOpen(false);
    setSelectedBlueprint(null);
    setSelectedGlossaryItem(null);
    setSelectedDialog(null);
  }, []);

  return {
    // States
    isAddWizardOpen,
    isEditModalOpen,
    isCreateWizardOpen,
    isBulkCreateWizardOpen,
    isDialogWizardOpen,
    selectedBlueprint,
    selectedGlossaryItem,
    selectedDialog,

    // Setters (for backward compatibility)
    setIsAddWizardOpen,
    setIsEditModalOpen,
    setIsCreateWizardOpen,
    setIsBulkCreateWizardOpen,
    setIsDialogWizardOpen,
    setSelectedBlueprint,
    setSelectedGlossaryItem,
    setSelectedDialog,

    // Open handlers
    openAddWizard,
    openEditModal,
    openCreateWizard,
    openBulkCreateWizard,
    openDialogWizard,

    // Close handlers
    closeAddWizard,
    closeEditModal,
    closeCreateWizard,
    closeBulkCreateWizard,
    closeDialogWizard,
    closeAllWizards,
  };
}

